/**
 * ═══════════════════════════════════════════════════════════════════════
 * useVisitMetadata — Visit-Scoped Metadata Hook with SyncEngine
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Replaces direct fetchVisitMetadata() calls in components with a
 * centralized, deduplicated, cache-aware hook.
 *
 * Fixes:
 *   • Infinite API call loops from unstable callback dependencies
 *   • Multiple components fetching same visit's metadata simultaneously
 *   • Ghost API calls after component unmount
 *   • Re-render driven fetch loops from onMetadataLoaded callback
 *   • Missing AbortController for in-flight cancellation
 *
 * Usage:
 *   const { metadata, loading, refresh } = useVisitMetadata(visit.visit_id);
 *
 * Features:
 *   • fetchOncePerVisit guard via SyncEngine cache
 *   • Auto-refresh on VISIT_METADATA_ADDED WebSocket events
 *   • Mount tracking prevents ghost calls
 *   • Stable callback identity (no re-render loops)
 * ═══════════════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import syncEngine from '../services/SyncEngine';
import { fetchVisitMetadata as apiFetchMetadata } from '../services/api/visitMetadataApi';
import eventBus from '../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';

// One WS event is delivered to every mounted watcher of the visit. Only the
// FIRST watcher per event drops the cache/in-flight request (so a post-write
// refetch never reuses a pre-write response); later watchers just force-fetch
// and join that fresh request -> 1 GET per event, not 1 per watcher.
const _invalidatedEvents = new WeakSet();
function invalidateOncePerEvent(event, visitId) {
  if (_invalidatedEvents.has(event)) return;
  _invalidatedEvents.add(event);
  syncEngine.invalidateMetadata(visitId);
}

/**
 * @param {string}  visitId   - Visit ID to fetch metadata for
 * @param {Object}  [opts]
 * @param {boolean} [opts.autoFetch=true]  - Fetch on mount
 * @param {boolean} [opts.autoRefreshOnWs=true] - Re-fetch on WS metadata events
 * @returns {{ metadata: Array, loading: boolean, error: Error|null, refresh: Function, fromCache: boolean }}
 */
export function useVisitMetadata(visitId, opts = {}) {
  const {
    autoFetch = true,
    autoRefreshOnWs = true,
  } = opts;

  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  // Mount tracking — prevents ghost calls and state updates after unmount
  const mountRef = useRef(null);
  const visitIdRef = useRef(visitId);
  visitIdRef.current = visitId;

  useEffect(() => {
    const mount = syncEngine.registerMount(`useVisitMetadata:${visitId}`);
    mountRef.current = mount;
    return () => {
      mount.unmount();
      // Abort any in-flight requests for this visit
      syncEngine.abortByPrefix(`metadata:${visitId}`);
    };
  }, [visitId]);

  // Stable fetch function — identity never changes
  const fetchMetadata = useCallback(async (force = false) => {
    const currentVisitId = visitIdRef.current;
    if (!currentVisitId) return [];

    const mount = mountRef.current;
    if (!mount || !mount.isMounted()) return [];

    setLoading(true);
    setError(null);

    try {
      const result = await syncEngine.fetchMetadata(
        currentVisitId,
        async (vid) => apiFetchMetadata(vid),
        {
          force,
          source: 'useVisitMetadata',
          isMounted: () => mount.isMounted(),
        }
      );

      if (!mount.isMounted()) return [];

      const entries = result?.data?.entries || [];
      setMetadata(entries);
      setFromCache(result?.fromCache || false);
      return entries;
    } catch (err) {
      if (mount.isMounted()) {
        setError(err);
        console.error('[useVisitMetadata] Fetch error:', err);
      }
      return [];
    } finally {
      if (mount.isMounted()) {
        setLoading(false);
      }
    }
  }, []); // No deps — uses refs for all dynamic values

  // Auto-fetch on mount / visitId change
  useEffect(() => {
    if (!autoFetch || !visitId) return;

    // Check for cached data first (synchronous)
    const cached = syncEngine.getCachedMetadata(visitId);
    if (cached) {
      setMetadata(cached.entries || []);
      setFromCache(true);
    }

    fetchMetadata(false);
  }, [visitId, autoFetch, fetchMetadata]);

  // Auto-refresh on WS VISIT_METADATA_ADDED
  useEffect(() => {
    if (!autoRefreshOnWs || !visitId) return;

    const unsub = eventBus.on(REALTIME_EVENTS.VISIT_METADATA_ADDED, (event) => {
      const eventVisitId = event.payload?.visitId;
      if (eventVisitId === visitIdRef.current && event.source !== 'local') {
        // Invalidate cache and re-fetch
        invalidateOncePerEvent(event, visitIdRef.current);
        fetchMetadata(true);
      }
    });

    return unsub;
  }, [visitId, autoRefreshOnWs, fetchMetadata]);

  // Phase 1b: re-fetch when a PRESCRIPTION entry's approval status flips.
  // Backend emits PRESCRIPTION_APPROVED / PRESCRIPTION_REJECTED on the
  // sitting's visit — we re-pull the whole metadata list so the Rx tab
  // badge + PDF gate flip live without a manual refresh.
  useEffect(() => {
    if (!autoRefreshOnWs || !visitId) return;

    const handler = (event) => {
      const eventVisitId = event.payload?.visit_id || event.payload?.visitId;
      if (eventVisitId === visitIdRef.current) {
        invalidateOncePerEvent(event, visitIdRef.current);
        fetchMetadata(true);
      }
    };
    const offApproved = eventBus.on(REALTIME_EVENTS.PRESCRIPTION_APPROVED, handler);
    const offRejected = eventBus.on(REALTIME_EVENTS.PRESCRIPTION_REJECTED, handler);
    return () => {
      offApproved();
      offRejected();
    };
  }, [visitId, autoRefreshOnWs, fetchMetadata]);

  // Stable refresh function for external callers
  const refresh = useCallback(() => {
    syncEngine.invalidateMetadata(visitIdRef.current);
    return fetchMetadata(true);
  }, [fetchMetadata]);

  return { metadata, loading, error, refresh, fromCache };
}

export default useVisitMetadata;
